import { Injectable } from '@angular/core';
import { ShopProduct, ShopSearchResponse } from '../../../../src/types';

/** Hosting rewrites this to the shopSearch function, the same endpoint the widget calls. */
export const SEARCH_API = '/api/search';

/**
 * Searches a shop the way its widget does, for the demo and the dashboard's
 * "try it". A new search cancels the one still in flight, so results never
 * arrive out of order while someone types.
 */
@Injectable({ providedIn: 'root' })
export class ShopSearchService {
   private inFlight?: AbortController;

   /** `ai`: let the AI read the query when the engine alone would misread it (see understand.ts). */
   async search(shopId: string, query: string, limit = 12, ai = false): Promise<ShopSearchResponse | null> {
      this.inFlight?.abort();
      const controller = new AbortController();
      this.inFlight = controller;
      const params = new URLSearchParams({ shop: shopId, q: query, limit: String(limit), ...(ai && { ai: '1' }) });
      try {
         const response = await fetch(`${SEARCH_API}?${params}`, { signal: controller.signal });
         if (!response.ok) throw new Error(`search ${response.status}`);
         return (await response.json()) as ShopSearchResponse;
      } catch (error) {
         if ((error as Error)?.name === 'AbortError') return null;
         throw error;
      }
   }

   /** A few searches to try, made from the shop's own catalogue ("шампоан", "shampoan", "шапмоан", "шампоан до 10 €"). */
   async examples(shopId: string): Promise<string[]> {
      const params = new URLSearchParams({ shop: shopId, examples: '1' });
      const response = await fetch(`${SEARCH_API}?${params}`);
      if (!response.ok) return [];
      return ((await response.json()) as { examples?: string[] }).examples ?? [];
   }

   /** Products like this one, as `<div data-oopsy-similar>` shows them on its page; none when the plan has no recommendations. */
   async similar(shopId: string, productId: string, limit = 4): Promise<ShopProduct[]> {
      const params = new URLSearchParams({ shop: shopId, similar: productId, limit: String(limit) });
      const response = await fetch(`${SEARCH_API}?${params}`);
      if (!response.ok) return [];
      return ((await response.json()) as ShopSearchResponse).results ?? [];
   }
}
